import React, { useState, useMemo, useCallback } from "react";
import ReactEChartsCore from "echarts-for-react/lib/core";
import * as echarts from "echarts/core";
import { BarChart } from "echarts/charts";
import {
  GridComponent,
  TooltipComponent,
  LegendComponent,
  MarkLineComponent,
  DataZoomComponent,
} from "echarts/components";
import { CanvasRenderer } from "echarts/renderers";
import { Box, Typography, Chip, IconButton, Tooltip } from "@mui/material";
import ZoomOutMapIcon from "@mui/icons-material/ZoomOutMap";
import { withCommas } from "./numberFormat";

echarts.use([
  BarChart,
  GridComponent,
  TooltipComponent,
  LegendComponent,
  MarkLineComponent,
  DataZoomComponent,
  CanvasRenderer,
]);

function formatCurrencyShort(value, { asBps = false, navNumber } = {}) {
  if (value == null || isNaN(value)) return "—";
  if (asBps && navNumber) {
    return `${((value / navNumber) * 10000).toFixed(1)} bps`;
  }
  const abs = Math.abs(value);
  const sign = value < 0 ? "-" : "";
  if (abs >= 1e6) return `${sign}$${withCommas(abs / 1e6)}M`;
  if (abs >= 1e3) return `${sign}$${withCommas(abs / 1e3, 0)}K`;
  return `${sign}$${abs.toFixed(0)}`;
}

const SIM_THRESHOLD = 100;

export default function PnlDistributionChart({
  distribution,
  percentiles,
  onSimClick,
  showBpsOfNav = false,
  navNumber = null,
}) {
  const [zoomRange, setZoomRange] = useState(null);
  const [zoomStack, setZoomStack] = useState([]);

  const sortedDistribution = useMemo(() => {
    if (!distribution || distribution.length === 0) return [];
    const sorted = [...distribution].sort((a, b) => a.pnl - b.pnl);
    // Exclude worst 10 and best 10 PnLs
    return sorted.slice(10, Math.max(10, sorted.length - 10));
  }, [distribution]);

  const filteredData = useMemo(() => {
    if (sortedDistribution.length === 0) return [];
    if (!zoomRange) return sortedDistribution;
    return sortedDistribution.filter(
      (d) => d.pnl >= zoomRange[0] && d.pnl <= zoomRange[1],
    );
  }, [sortedDistribution, zoomRange]);

  const isSimMode = filteredData.length <= SIM_THRESHOLD && filteredData.length > 0;

  const option = useMemo(() => {
    if (filteredData.length === 0) return null;

    const p1Pnl = percentiles?.p1?.pnl;
    const p5Pnl = percentiles?.p5?.pnl;
    const p50Pnl = percentiles?.p50?.pnl;
    const p95Pnl = percentiles?.p95?.pnl;
    const p99Pnl = percentiles?.p99?.pnl;

    function getBarColor(pnlValue) {
      if (p1Pnl != null && pnlValue <= p1Pnl) return "#c53030";
      if (p5Pnl != null && pnlValue <= p5Pnl) return "#e06040";
      if (p50Pnl != null && pnlValue <= p50Pnl) return "#d4a843";
      if (p95Pnl != null && pnlValue <= p95Pnl) return "#6da87a";
      return "#006842";
    }

    // Individual sim bar chart: one bar per simulation
    if (isSimMode) {
      const sorted = [...filteredData].sort((a, b) => a.pnl - b.pnl);
      return {
        tooltip: {
          trigger: "axis",
          axisPointer: { type: "shadow" },
          formatter: (params) => {
            const p = params[0];
            if (!p) return "";
            const sim = sorted[p.dataIndex];
            if (!sim) return "";
            return `<b>Sim #${sim.sim_number}</b><br/>PnL: <b>${formatCurrencyShort(sim.pnl, { asBps: showBpsOfNav, navNumber })}</b><br/><span style="font-size:11px;color:#888">Click for strategy breakdown</span>`;
          },
        },
        grid: { left: 60, right: 20, top: 20, bottom: 40 },
        xAxis: {
          type: "category",
          data: sorted.map((d) => `#${d.sim_number}`),
          axisLabel: {
            fontSize: 10,
            rotate: sorted.length > 15 ? 45 : 0,
            interval: sorted.length > 30 ? Math.floor(sorted.length / 20) : 0,
          },
          axisTick: { show: false },
          axisLine: { lineStyle: { color: "#ddd" } },
        },
        yAxis: {
          type: "value",
          name: "PnL",
          nameLocation: "center",
          nameGap: 45,
          axisLabel: { fontSize: 11, formatter: (v) => formatCurrencyShort(v, { asBps: showBpsOfNav, navNumber }) },
          splitLine: { lineStyle: { color: "#f0f0f0" } },
        },
        series: [
          {
            type: "bar",
            data: sorted.map((d) => ({
              value: d.pnl,
              itemStyle: {
                color: getBarColor(d.pnl),
                borderRadius: d.pnl >= 0 ? [3, 3, 0, 0] : [0, 0, 3, 3],
              },
            })),
            barMinWidth: 3,
            barCategoryGap: "20%",
            emphasis: {
              itemStyle: { shadowBlur: 10, shadowColor: "rgba(0,0,0,0.25)" },
            },
          },
        ],
        _sims: sorted,
      };
    }

    // Standard distribution histogram: bin by PnL range, y-axis = frequency
    const pnlValues = filteredData.map((d) => d.pnl);
    const minPnl = Math.min(...pnlValues);
    const maxPnl = Math.max(...pnlValues);
    const range = maxPnl - minPnl;
    const binCount = Math.min(60, Math.max(25, Math.ceil(Math.sqrt(filteredData.length))));
    const binWidth = range / binCount || 1;

    const bins = Array.from({ length: binCount }, (_, i) => ({
      start: minPnl + i * binWidth,
      end: minPnl + (i + 1) * binWidth,
      count: 0,
      sims: [],
    }));

    filteredData.forEach((d) => {
      const idx = Math.min(
        Math.floor((d.pnl - minPnl) / binWidth),
        binCount - 1,
      );
      if (idx >= 0 && idx < bins.length) {
        bins[idx].count++;
        bins[idx].sims.push(d);
      }
    });

    const markLines = [];
    const addMark = (pnl, label, color, type = "dashed") => {
      if (pnl == null || pnl < minPnl || pnl > maxPnl) return;
      const binIdx = Math.min(Math.floor((pnl - minPnl) / binWidth), binCount - 1);
      markLines.push({
        xAxis: binIdx,
        label: { show: true, formatter: label, fontSize: 10, position: "end", color },
        lineStyle: { color, type, width: 1.5 },
      });
    };
    addMark(p1Pnl, "P1", "#c53030");
    addMark(p5Pnl, "P5", "#e06040");
    addMark(p50Pnl, "P50", "#91B6C0", "solid");
    addMark(p95Pnl, "P95", "#006842");
    addMark(p99Pnl, "P99", "#004d31");

    return {
      tooltip: {
        trigger: "axis",
        axisPointer: { type: "shadow" },
        formatter: (params) => {
          const p = params[0];
          if (!p) return "";
          const bin = bins[p.dataIndex];
          if (!bin) return "";
          return [
            `<b>${formatCurrencyShort(bin.start, { asBps: showBpsOfNav, navNumber })} to ${formatCurrencyShort(bin.end, { asBps: showBpsOfNav, navNumber })}</b>`,
            `${bin.count} simulation${bin.count !== 1 ? "s" : ""}`,
            `<span style="font-size:11px;color:#888">Click to zoom in</span>`,
          ].join("<br/>");
        },
      },
      grid: { left: 50, right: 20, top: 25, bottom: 50 },
      xAxis: {
        type: "category",
        data: bins.map((b) => formatCurrencyShort((b.start + b.end) / 2, { asBps: showBpsOfNav, navNumber })),
        axisLabel: {
          fontSize: 10,
          rotate: bins.length > 30 ? 45 : 0,
          interval: Math.max(0, Math.floor(bins.length / 12)),
        },
        axisTick: { show: false },
        axisLine: { lineStyle: { color: "#ccc" } },
        name: "PnL",
        nameLocation: "center",
        nameGap: 35,
        nameTextStyle: { fontSize: 11, color: "#888" },
        triggerEvent: true,
      },
      yAxis: {
        type: "value",
        name: "Frequency",
        nameLocation: "center",
        nameGap: 35,
        axisLabel: { fontSize: 11 },
        splitLine: { lineStyle: { color: "#f5f5f5" } },
        axisLine: { show: false },
      },
      series: [
        {
          type: "bar",
          data: bins.map((bin) => ({
            value: bin.count,
            itemStyle: {
              color: getBarColor((bin.start + bin.end) / 2),
              borderRadius: [2, 2, 0, 0],
            },
          })),
          barCategoryGap: "0%",
          barGap: "0%",
          emphasis: {
            itemStyle: {
              shadowBlur: 6,
              shadowColor: "rgba(0,0,0,0.15)",
              borderColor: "#333",
              borderWidth: 1,
            },
          },
          markLine: {
            silent: true,
            symbol: "none",
            data: markLines,
          },
        },
      ],
      _bins: bins,
    };
  }, [filteredData, percentiles, isSimMode, showBpsOfNav, navNumber]);

  const handleClick = useCallback(
    (params) => {
      if (isSimMode) {
        if (params.dataIndex != null && option?._sims && onSimClick) {
          const sim = option._sims[params.dataIndex];
          if (sim) onSimClick(sim.sim_number, sim.pnl);
        }
        return;
      }

      const idx = params.dataIndex;
      if (idx == null || !option?._bins) return;
      const bin = option._bins[idx];
      if (!bin || bin.count === 0) return;

      setZoomStack((prev) => [...prev, zoomRange]);
      setZoomRange([bin.start, bin.end]);
    },
    [isSimMode, option, onSimClick, zoomRange],
  );

  const handleZoomOut = () => {
    if (zoomStack.length > 0) {
      const prev = zoomStack[zoomStack.length - 1];
      setZoomStack((s) => s.slice(0, -1));
      setZoomRange(prev);
    } else {
      setZoomRange(null);
    }
  };

  const handleReset = () => {
    setZoomRange(null);
    setZoomStack([]);
  };

  if (!distribution || distribution.length === 0) {
    return (
      <Box sx={{ py: 4, textAlign: "center" }}>
        <Typography variant="caption" color="text.disabled">
          No distribution data available
        </Typography>
      </Box>
    );
  }

  if (!option) {
    return (
      <Box sx={{ py: 4, textAlign: "center" }}>
        <Typography variant="caption" color="text.disabled">
          No data in current range
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Zoom controls */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
        <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.75rem" }}>
          {distribution.length.toLocaleString()} sims
          {isSimMode
            ? " — click a bar for strategy breakdown"
            : " — click a bar to zoom in"}
        </Typography>
        {zoomRange && (
          <>
            <Chip
              label={`${formatCurrencyShort(zoomRange[0], { asBps: showBpsOfNav, navNumber })} → ${formatCurrencyShort(zoomRange[1], { asBps: showBpsOfNav, navNumber })}`}
              size="small"
              onDelete={handleZoomOut}
              sx={{ fontSize: "0.7rem", height: 22 }}
            />
            <Tooltip title="Reset zoom">
              <IconButton size="small" onClick={handleReset} sx={{ p: 0.5 }}>
                <ZoomOutMapIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          </>
        )}
      </Box>

      {/* Percentile legend */}
      {!zoomRange && (
        <Box sx={{ display: "flex", gap: 1, mb: 0.5, flexWrap: "wrap" }}>
          {[
            { key: "p1", label: "P1 (1% tail)", color: "#c53030" },
            { key: "p5", label: "P5", color: "#e06040" },
            { key: "p50", label: "P50 (median)", color: "#91B6C0" },
            { key: "p95", label: "P95", color: "#006842" },
            { key: "p99", label: "P99", color: "#004d31" },
          ].map((item) => (
            <Box key={item.key} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: item.color }} />
              <Typography variant="caption" sx={{ fontSize: "0.65rem", color: "text.secondary" }}>
                {item.label}
              </Typography>
            </Box>
          ))}
        </Box>
      )}

      <Box sx={{ width: "100%", minHeight: 0 }}>
        <ReactEChartsCore
          echarts={echarts}
          option={option}
          style={{ height: "clamp(220px, 30vw, 320px)", width: "100%" }}
          onEvents={{ click: handleClick }}
          notMerge
        />
      </Box>
    </Box>
  );
}
